import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';

/**
 * PageCard - Layout padrão de página com card e barra de título.
 *
 * Props:
 *   title       {string}    - Título exibido na barra
 *   icon        {string}    - Classe do ícone Bootstrap (ex: "bi-bank")
 *   onClose     {function}  - Ação do botão fechar (padrão: navega para /home)
 *   footer      {ReactNode} - Conteúdo fixo no rodapé do card (ex: paginação)
 *   showHeader  {boolean}   - Exibe/oculta barra de título e botão fechar
 *   scrollable  {boolean}   - Se true (padrão), o conteúdo principal tem scroll.
 *                             Use false quando a tabela interna já tem seu próprio scroll.
 *   children    {ReactNode} - Conteúdo da página
 */
function PageCard({
  title,
  icon = 'bi-file-earmark',
  onClose,
  footer,
  showHeader = true,
  scrollable = true,
  embedded = false,
  className = '',
  style = {},
  children,
}) {
  const navigate = useNavigate();
  const handleClose = onClose || (() => navigate('/home'));
  const outerRef = useRef(null);
  const [cardHeight, setCardHeight] = useState('calc(100vh - 150px)');

  useEffect(() => {
    if (embedded) return;

    function computeHeight() {
      try {
        const el = outerRef.current;
        const header = document.querySelector('.navbar');
        const footer = document.querySelector('footer.footer') || document.querySelector('footer');
        const elTop = el ? el.getBoundingClientRect().top : 80;
        const headerBottom = header ? header.getBoundingClientRect().bottom : 80;
        const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight - 40;
        // Mantém a folga inferior igual à folga superior (entre navbar e janela)
        const topGap = Math.max(0, elTop - headerBottom);
        const bottomGap = Math.max(20, topGap);
        const available = Math.floor(footerTop - elTop - bottomGap);
        setCardHeight(`${Math.max(200, available)}px`);
      } catch (e) {
        setCardHeight('calc(100vh - 150px)');
      }
    }

    computeHeight();
    window.addEventListener('resize', computeHeight);
    const mo = new MutationObserver(computeHeight);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener('resize', computeHeight);
      mo.disconnect();
    };
  }, [embedded]);

  const cardContent = (
    <div
      ref={outerRef}
      className={className}
      style={
        embedded
          ? { display: 'flex', flexDirection: 'column', minHeight: 0, ...style }
          : { height: cardHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...style }
      }
    >
      <div className="card shadow-sm" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {showHeader && (
            <div
              className="card-header d-flex align-items-center justify-content-between"
              style={{
                background: 'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                color: '#fff',
                padding: '4px 12px',
                minHeight: '40px',
                flexShrink: 0,
              }}
            >
              <div className="d-flex align-items-center">
                <i className={`bi ${icon} me-2`}></i>
                <h5 className="mb-0">{title}</h5>
              </div>
              <button
                className="btn d-inline-flex align-items-center"
                style={{ color: '#fff', borderRadius: '50%', width: 32, height: 32, justifyContent: 'center' }}
                title="Fechar"
                onClick={handleClose}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}

          {/* Conteúdo */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: scrollable ? 'auto' : 'hidden',
                padding: '12px 16px',
                boxSizing: 'border-box',
              }}
            >
              {children}
            </div>

            {/* Footer fixo do card (ex: paginação) */}
            {footer && (
              <div
                style={{
                  flexShrink: 0,
                  borderTop: '1px solid #dee2e6',
                  padding: '6px 16px',
                  background: '#f8f9fa',
                }}
              >
                {footer}
              </div>
            )}

            {!footer && <div style={{ height: '15px' }}></div>}
          </div>
      </div>
    </div>
  );

  if (embedded) {
    return cardContent;
  }

  return (
    <Layout>
      {cardContent}
    </Layout>
  );
}

export default PageCard;

